import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { EasyPayEditForm } from 'containers/Premiums/components/EasyPayEditForm/EasyPayEditForm'
import PrivacyActNotice from 'containers/Premiums/components/EasyPay/PrivacyActNotice/PrivacyActNotice'
import { EasyPayEditFormData } from 'containers/Premiums/components/EasyPayEditForm/schema'
import { useEasyPayChange, useEasyPaySummary } from 'queries/premiums'
import { Component, PremiumsEasyPayAccountType } from '@cms-max/medicare-api-sdk'
import { Redirect, useHistory } from 'react-router'
import EasyPayServerErrorAlert from 'containers/Premiums/components/EasyPay/EasyPayServerErrorAlert/EasyPayServerErrorAlert'
import { SplitPageLayout } from 'containers/Premiums/components/SplitPageLayout/SplitPageLayout'
import { BackToEasyPayBreadcrumb } from 'containers/Premiums/components/EasyPay/BackToEasyPayBreadcrumb'
import { useEvent } from 'hooks/useEvent'

export const EasyPayEditPage: FC = () => {
  const { t } = useTranslation('easyPayEditPage')
  const history = useHistory()
  const { fireEvent } = useEvent('premiumsEasyPay')

  const {
    mutate,
    data: mutationResult,
    isLoading: isMutating,
    isSuccess,
    isError: isMutationError,
    error
  } = useEasyPayChange()

  const handleCancel = () => {
    history.push('/premiums/easy-pay')
  }
  const handleSubmit = async (formData: EasyPayEditFormData) => {
    fireEvent('premiums_easypaychangebank_submit_click')
    await mutate({
      accountNumber: formData.accountNumber,
      routingNumber: formData.routing,
      accountType: formData.accountType as PremiumsEasyPayAccountType
    })
  }

  // If we haven't fetched the summary data, redirect back to the summary page.
  const { isFetched } = useEasyPaySummary()
  if (!isFetched) {
    return <Redirect to="/premiums/easy-pay" />
  }

  if (isSuccess) {
    return (
      <Redirect
        to={{
          pathname: '/premiums/easy-pay/edit/submitted',
          search: `?date=${mutationResult?.completedAt}`
        }}
      />
    )
  }

  if (
    isMutationError &&
    ['409', '503'].includes(String(error?.response?.status))
  ) {
    return (
      <Redirect
        to={{
          pathname: '/premiums/easy-pay/error',
          search: `?code=${
            error?.response?.status === 409 ? 'ineligible' : 'unavailable'
          }`
        }}
      />
    )
  }

  return (
    <SplitPageLayout
      heading={t('heading')}
      nav={<BackToEasyPayBreadcrumb />}
      left={
        <>
          <h2 className="ds-text-heading--xl">{t('info.heading')}</h2>
          <Trans t={t} i18nKey={'info.content'} />
        </>
      }
      right={
        <>
          <h2 className="ds-text-heading--xl">{t('form.heading')}</h2>
          <p>{t('form.allRequired')}</p>
          <EasyPayEditForm
            pageType='easypaychangebank'
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            loading={isMutating}
          />

          {isMutationError && (
            <EasyPayServerErrorAlert className="ds-u-margin-top--3" />
          )}
        </>
      }
      bottom={<PrivacyActNotice />}
      printComponent={Component.EasyPay}
    />
  )
}
